import { styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { GridColDef } from '@mui/x-data-grid-premium';
import { HorizontalScrollableContainer } from 'common/components/container';
import { FloatingMenuItem } from 'common/components/core/FloatingMenu';
import { CXAvatar } from 'common/components/cx-avatar/cx-avatar';
import CXDataGridManageColumnsButton from 'common/components/cx-data-grid/cx-data-grid-manage-columns-button.component';
import { FormDrawer } from 'common/components/drawer';
import NoResultsImage from 'common/components/image/NoResultsImage';
import { CommonStyledLink } from 'common/components/links/styles/common-link.style';
import { Box, Button, Card, Typography } from 'common/components/material';
import { CardContent } from 'common/components/material/Card';
import { DrawerHeader } from 'common/components/material/Drawer';
import { CloseButton } from 'common/components/navigation';
import { PersonLink } from 'common/components/person-link/person-link.component';
import { useDrawer } from 'common/hooks';
import AppRoutesEnum from 'common/routes/AppRoutes.enum';
import axios from 'common/utils/axios';
import SkillChip from 'features/org-root/components/SkillChip';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import { useGetProfile } from 'features/profiles';
import { Skill } from 'features/skills';
import { GridColumn } from 'libs/mui/data-grid/types/data-grid-props.type';
import { CXViewer } from 'libs/toast-ui';
import _, { get } from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import * as yup from 'yup';
import {
  ArchiveButton,
  DownvoteButton,
  UpvoteButton,
} from './components/SkillEndorsementButtons';
import { PastFeedbackDropdownMenu } from './components/SkillFeedbackCard';

export const matchingEngineApi = axios('matching-engine');

export const DrawerTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h3,
  fontWeight: 700,
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

export enum SkillStatus {
  VERIFIED = 'Verified',
  MAYBE = 'Maybe',
  MISSING = 'Missing',
}

export const PEOPLE_SEARCH_PARAM = 'people';
export const CRITICAL_PEOPLE_SEARCH_PARAM = 'excludeCriticalPeople';
export const UNVERIFIED_SKILL_SEARCH_PARAM = 'excludeUnverifiedSkills';

export enum PEOPLE_COMPARISON_COLUMN_FIELDS {
  CRITICAL_CURRENT_ALLOCATION = 'Critical',
  MAJOR_CURRENT_ALLOCATION = 'Major',
  LOW_CURRENT_ALLOCATION = 'Low',
  CONFIRMED_SKILLS = 'Confirmed Skills',
  MAYBE_SKILLS = 'Maybe Skills',
  MISSING_SKILLS = 'Missing Skills',
  MANAGER = 'Manager',
  L2MANAGER = 'L2 Manager',
  L3MANAGER = 'L3 Manager',
  LOCATION = 'Location',
  DEPARTMENT = 'Department',
  DIVISION = 'Division',
  PRODUCT = 'Product',
}

export const FeedbackFormSchema = () => {
  return yup.object().shape({
    feedback: yup.string().label('feedback').required('Feedback is required'),
  });
};

export type AddFeedbackParams = {
  revieweeId: string;
  skillIds: string[];
  feedback: string;
};

export type FeedbackForm = {
  feedback: string;
};

export type PastFeedback = {
  id: string;
  feedback: string;
  createdDate: string;
  skills: Skill[];
};

export enum MatchingEngineContributorDetailDrawer {
  CurrentAllocation = 'current-allocation',
  TrackRecord = 'track-record',
  Feedback = 'feedback',
}

export enum VoteType {
  UPVOTE = 'UPVOTE',
  DOWNVOTE = 'DOWNVOTE',
  ARCHIVE = 'ARCHIVE',
}

export const MATCHING_ENGINE_PERSON_DRAWER_ID = DrawerIdEnum.MATCHING_ENGINE_PERSON;

export const NoMatchFound: React.FC = () => {
  const { palette } = useTheme();

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={2.5}
      pt={4.5}
      data-analytics-tag="search_no_results_found"
    >
      <Stack spacing={1.5} alignItems="center" justifyContent="center">
        <NoResultsImage />
        <Typography variant="body2" fontWeight={500} color={palette.Text.Headline}>
          Nothing matched!
        </Typography>
        <Typography variant="body3" color={palette.Text.SubHeadline} textAlign="center">
          Try refining your search.
        </Typography>
      </Stack>
    </Stack>
  );
};

export function extractPersonSummary(row: any) {
  return {
    name: row.name,
    photo: row.photo,
    profileID: row.profileID,
    stepID: row.stepID,
    feedbacks: row.feedbacks,
    skillID: row.skillID,
  };
}

export const findSkillsSortedByRarity = (
  searchedSkills: { id: string; name: string }[],
  people: any[],
) => {
  if (searchedSkills.length <= 1) return searchedSkills;
  const skillFrequencyMap: Record<string, number> = {};

  for (const person of people) {
    const personSkillIds = new Set(person.skills.map((s: { id: string }) => s.id));
    for (const skill of searchedSkills) {
      if (!personSkillIds.has(skill.id)) continue;
      skillFrequencyMap[skill.id] = (skillFrequencyMap[skill.id] || 0) + 1;
    }
  }

  const sortedByNiche = [...searchedSkills].sort((a, b) => {
    const countA = skillFrequencyMap[a.id] || 0;
    const countB = skillFrequencyMap[b.id] || 0;
    return countA - countB; // Ascending: least common first
  });

  return sortedByNiche;
};

type GridCellItemProps = {
  value: any;
  onClick?: (e: React.MouseEvent) => void;
  readonly: boolean;
  alignCenter?: boolean;
};

export const GridCellItem: React.FC<GridCellItemProps> = ({
  value,
  onClick,
  readonly = false,
  alignCenter = false,
}) => {
  if (_.isNil(value) || value === '') return null;

  return (
    <Stack
      alignItems={alignCenter ? 'center' : undefined}
      justifyContent={alignCenter ? 'center' : undefined}
      px={1}
      sx={{
        width: '100%',
        height: '100%',
        cursor: readonly ? 'default' : 'pointer',
        '&:hover': readonly
          ? undefined
          : {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
      }}
      onClick={onClick}
    >
      <Typography
        variant="body4"
        sx={{
          color: (theme) => theme.palette.LinkText.Default,
          fontWeight: 500,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
};

const getCurrentAllocation = ({
  itemsCount,
  highCriticalityCount,
}: {
  itemsCount: number;
  highCriticalityCount: number;
}) => {
  if (!itemsCount || itemsCount === 0) {
    return '';
  }

  if (itemsCount === 1) {
    return `Working on 1 item`;
  }

  const criticalLabel =
    highCriticalityCount && highCriticalityCount > 0
      ? `(${highCriticalityCount} Critical)`
      : '';

  return `Working on ${itemsCount} items ${criticalLabel}`.trim();
};

export const usePeopleGridColumns = (): GridColumn[] => {
  const { openDrawer } = useDrawer();

  return [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: ({ row }) => {
        return (
          <PersonLink
            key={row.profileID}
            id={row.profileID}
            name={row.name}
            color="inherit"
            fontWeight={500}
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'name');
      },
    },
    {
      field: 'currentAllocation',
      headerName: 'Current Allocation',
      width: 250,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            value={getCurrentAllocation(row.allocationMetadata)}
            readonly={false}
            onClick={() =>
              openDrawer(MATCHING_ENGINE_PERSON_DRAWER_ID, {
                person: extractPersonSummary(row),
                initialTab: MatchingEngineContributorDetailDrawer.CurrentAllocation,
              })
            }
            alignCenter={false}
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'allocationMetadata.highCriticalityCount');
      },
    },
    {
      field: 'numberOfContributions',
      headerName: 'Number of Contributions',
      width: 200,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            value={row.numberOfContributions}
            readonly={false}
            onClick={() =>
              openDrawer(MATCHING_ENGINE_PERSON_DRAWER_ID, {
                person: extractPersonSummary(row),
                initialTab: MatchingEngineContributorDetailDrawer.TrackRecord,
              })
            }
            alignCenter={true}
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'numberOfContributions');
      },
    },
    {
      field: 'lastFeedbackDate',
      headerName: 'Last Feedback Date',
      width: 150,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            value={row.lastFeedbackDate}
            readonly={false}
            onClick={() =>
              openDrawer(MATCHING_ENGINE_PERSON_DRAWER_ID, {
                person: extractPersonSummary(row),
                initialTab: MatchingEngineContributorDetailDrawer.Feedback,
              })
            }
            alignCenter={true}
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'lastFeedbackDate');
      },
    },
    {
      field: 'manager',
      headerName: 'Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.manager?.id ? (
          <PersonLink
            key={row.manager.id}
            id={row.manager.id}
            name={row.manager.fullName}
            color="inherit"
            fontWeight={500}
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'manager.fullName');
      },
    },
    {
      field: 'l2manager',
      headerName: 'L2 Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.l2Manager?.id ? (
          <PersonLink
            key={row.l2Manager.id}
            id={row.l2Manager.id}
            name={row.l2Manager.fullName}
            color="inherit"
            fontWeight={500}
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'l2Manager.fullName');
      },
    },
    {
      field: 'l3manager',
      headerName: 'L3 Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.l3Manager?.id ? (
          <PersonLink
            key={row.l3Manager.fullName}
            id={row.l3Manager.id}
            name={row.l3Manager.fullName}
            color="inherit"
            fontWeight={500}
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'l3Manager.fullName');
      },
    },
    {
      field: 'gridActions',
      headerClassName: 'gridActions',
      renderHeader: () => <CXDataGridManageColumnsButton />,
      width: 40,
      minWidth: 40,
      sortable: false,
      resizable: false,
      pinnable: false,
      hideable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
  ];
};

type StaticLinkColumnOptions = {
  field: string;
  headerName: string;
  width?: number;
  staticDataType: string;
};

export const getStaticLinkColumn = ({
  field,
  headerName,
  width = 200,
  staticDataType,
}: StaticLinkColumnOptions): GridColDef => ({
  field,
  headerName,
  width,
  renderCell: ({ row }) => {
    const entity = row[field];

    if (!entity) return '';

    return (
      <GridCellItem
        readonly={true}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
        }}
        value={
          <CommonStyledLink
            color="inherit"
            sx={{ fontWeight: 500 }}
            href={AppRoutesEnum.STATIC_DATA_SUMMARY}
            params={{ staticDataType, staticDataId: `${entity.id}` }}
            variant="body4"
          >
            {entity.displayName}
          </CommonStyledLink>
        }
      />
    );
  },
  valueGetter: (value, row) => get(row, `${field}.displayName`),
});

export const SkillFilterGridGroupingModel = [
  {
    groupId: 'currentAllocation',
    headerName: 'Current Allocation',
    children: [{ field: 'critical' }, { field: 'major' }, { field: 'low' }],
  },
  {
    groupId: 'skills',
    headerName: 'Skills',
    children: [{ field: 'confirmed' }, { field: 'maybe' }, { field: 'missing' }],
  },
];

export const useSkillFilterGridColumns = (): GridColumn[] => {
  const { openDrawer } = useDrawer();
  return [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: ({ row }) => {
        return (
          <PersonLink
            key={row.profileID}
            id={row.profileID}
            name={row.name}
            color="inherit"
            fontWeight={500}
          />
        );
      },
    },
    {
      field: 'critical',
      headerName: 'Critical',
      headerAlign: 'center',
      width: 75,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            readonly={false}
            value={row.currentAllocationCriticality?.highCriticalityCount}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              openDrawer(MATCHING_ENGINE_PERSON_DRAWER_ID, {
                person: extractPersonSummary(row),
                initialTab: MatchingEngineContributorDetailDrawer.CurrentAllocation,
              });
            }}
            alignCenter={true}
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'currentAllocationCriticality.highCriticalityCount');
      },
    },
    {
      field: 'major',
      headerName: 'Major',
      headerAlign: 'center',
      width: 75,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            readonly={false}
            value={row.currentAllocationCriticality.mediumCriticalityCount}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              openDrawer(MATCHING_ENGINE_PERSON_DRAWER_ID, {
                person: extractPersonSummary(row),
                initialTab: MatchingEngineContributorDetailDrawer.CurrentAllocation,
              });
            }}
            alignCenter={true}
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'currentAllocationCriticality.mediumCriticalityCount');
      },
    },
    {
      field: 'low',
      headerName: 'Low',
      headerAlign: 'center',
      width: 75,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            readonly={false}
            value={row.currentAllocationCriticality.lowCriticalityCount}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              openDrawer(MATCHING_ENGINE_PERSON_DRAWER_ID, {
                person: extractPersonSummary(row),
                initialTab: MatchingEngineContributorDetailDrawer.CurrentAllocation,
              });
            }}
            alignCenter={true}
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'currentAllocationCriticality.lowCriticalityCount');
      },
    },
    {
      field: 'confirmed',
      headerName: 'Confirmed',
      headerAlign: 'center',
      minWidth: 90,
      flex: 0,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            readonly={true}
            value={row.matchedSkillsTracker.confirmed}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
            alignCenter={true}
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'matchedSkillsTracker.confirmed');
      },
    },
    {
      field: 'maybe',
      headerName: 'Maybe',
      headerAlign: 'center',
      width: 80,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            readonly={true}
            value={row.matchedSkillsTracker.maybe}
            alignCenter={true}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'matchedSkillsTracker.maybe');
      },
    },
    {
      field: 'missing',
      headerName: 'Missing',
      headerAlign: 'center',
      width: 80,
      renderCell: ({ row }) => {
        return (
          <GridCellItem
            readonly={true}
            value={row.matchedSkillsTracker.missing}
            alignCenter={true}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
          />
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'matchedSkillsTracker.missing');
      },
    },
    {
      field: 'manager',
      headerName: 'Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.manager?.id ? (
          <GridCellItem
            readonly={true}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
            value={
              <PersonLink
                key={row.manager.id}
                id={row.manager.id}
                name={row.manager.fullName}
                color="inherit"
                fontWeight={500}
              />
            }
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'manager.fullName');
      },
    },
    {
      field: 'l2manager',
      headerName: 'L2 Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.l2Manager?.id ? (
          <GridCellItem
            readonly={true}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
            value={
              <PersonLink
                key={row.l2Manager.id}
                id={row.l2Manager.id}
                name={row.l2Manager.fullName}
                color="inherit"
                fontWeight={500}
              />
            }
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'l2Manager.fullName');
      },
    },
    {
      field: 'l3manager',
      headerName: 'L3 Manager',
      width: 200,
      renderCell: ({ row }) => {
        return row.l3Manager?.id ? (
          <GridCellItem
            readonly={true}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
            value={
              <PersonLink
                key={row.l3Manager.fullName}
                id={row.l3Manager.id}
                name={row.l3Manager.fullName}
                color="inherit"
                fontWeight={500}
              />
            }
          />
        ) : (
          ''
        );
      },
      valueGetter: (value, row) => {
        return get(row, 'l3Manager.fullName');
      },
    },
    getStaticLinkColumn({
      field: 'location',
      headerName: 'Location',
      staticDataType: 'WORK_LOCATION',
    }),
    getStaticLinkColumn({
      field: 'department',
      headerName: 'Department',
      staticDataType: 'DEPARTMENT',
    }),
    getStaticLinkColumn({
      field: 'division',
      headerName: 'Division',
      staticDataType: 'DIVISION',
    }),
    getStaticLinkColumn({
      field: 'product',
      headerName: 'Product',
      staticDataType: 'PRODUCT',
    }),
    {
      field: 'gridActions',
      headerClassName: 'gridActions',
      renderHeader: () => <CXDataGridManageColumnsButton />,
      // This is required to make the cells under the grid action toolbar not act as checkboxes.
      renderCell: () => {
        return (
          <GridCellItem
            readonly={true}
            value={' '}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
          />
        );
      },
      width: 40,
      minWidth: 40,
      sortable: false,
      resizable: false,
      pinnable: false,
      hideable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
  ];
};

export const getDateLabel = (date: Date) => {
  const todayDate = moment.utc().startOf('day');
  const days = moment.duration(todayDate.diff(moment.utc(date).startOf('day'))).asDays();

  if (days === 0) {
    return 'Today';
  }
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
};

export const FeedbackList: React.FC<{
  feedbacks: any[];
}> = ({ feedbacks }) => {
  const [visibleFeedbacks, setVisibleFeedbacks] = useState(3);
  const showMoreFeedbacks = () => {
    setVisibleFeedbacks((prev) => Math.min(prev + 3, feedbacks.length));
  };

  if (!feedbacks) return <></>;

  return (
    <Stack gap={1} mt={2} width="100%">
      {!feedbacks.length && (
        <Typography variant="body4" sx={{ fontStyle: 'italic' }}>
          No manager feedback found
        </Typography>
      )}
      <Stack spacing={2}>
        {feedbacks.slice(0, visibleFeedbacks).map((feedback: any) => (
          <FeedbackCard key={feedback.id} feedback={feedback} />
        ))}

        {visibleFeedbacks < feedbacks.length && (
          <Button onClick={showMoreFeedbacks}>See More</Button>
        )}
        {feedbacks.length > 3 && visibleFeedbacks >= feedbacks.length && (
          <Button onClick={() => setVisibleFeedbacks(3)}>See Less</Button>
        )}
      </Stack>
    </Stack>
  );
};

export const FeedbackCard: React.FC<{ feedback: any }> = ({ feedback }) => {
  const { palette } = useTheme();

  const {
    id,
    skills,
    createdDate,
    reviewer,
    feedback: feedbackText,
    skillId,
    voteType,
    revieweeId,
  } = feedback;

  const headers = [`${getDateLabel(createdDate)}`, `from ${reviewer.fullName}`];

  return (
    <Card noElevation sx={{ ml: 1 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color={palette.Text.SubHeadline}>
            {headers.join(' • ')}
          </Typography>
          {skillId && (
            <PastFeedbackDropdownMenu
              profileId={revieweeId}
              feedbackId={id}
              skillId={skillId}
            />
          )}
        </Stack>

        <Stack flexDirection="row">
          {voteType === 'UPVOTE' && <UpvoteButton isSelected />}
          {voteType === 'DOWNVOTE' && <DownvoteButton isSelected />}
          {voteType === 'ARCHIVE' && <ArchiveButton isSelected />}
          <Box sx={{ width: '95%' }}>
            <CXViewer value={feedbackText} isInitiallyExpanded={true} />
          </Box>
        </Stack>
        {skills && (
          <HorizontalScrollableContainer
            scrolledListElement={
              <Stack direction="row" gap={2}>
                {skills.map((skill: Skill) => (
                  <SkillChip key={skill.id} skill={skill} readonly={true} />
                ))}
              </Stack>
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

export const FEEDBACK_MENU_OPTIONS: FloatingMenuItem[] = [
  {
    value: 'delete',
    label: 'Delete',
    palettePath: 'Failure',
  },
];

export const PastFeedbackDrawer: React.FC<{
  revieweeId: any;
  drawerId: DrawerIdEnum;
  title: string;
  children: React.ReactNode;
}> = ({ revieweeId, drawerId, children, title }) => {
  const { closeDrawer } = useDrawer();
  const { palette } = useTheme();

  const { data: profile, isLoading } = useGetProfile({ id: revieweeId });

  if (isLoading) return <></>;

  const closeCurrentDrawer = () => {
    closeDrawer(drawerId);
  };

  const header = (
    <DrawerHeader
      sx={{
        borderBottom: `1px solid ${palette.Divider}`,
        padding: 2,
      }}
    >
      <Stack spacing={2} width="100%">
        <Stack justifyContent="space-between" alignItems="center" flexDirection="row">
          <Stack alignItems="center" flexDirection="row">
            <CXAvatar src={profile?.photo} alt={profile?.fullName} />
            <DrawerTitle sx={{ ml: 2 }}>{profile.fullName}</DrawerTitle>
          </Stack>

          <CloseButton clickHandler={closeCurrentDrawer} />
        </Stack>

        <Typography variant="body1" fontWeight={500}>
          {title}
        </Typography>
      </Stack>
    </DrawerHeader>
  );

  return (
    <FormDrawer header={header}>
      <Box mx={3} mt={2} display="flex" height="100%">
        {children}
      </Box>
    </FormDrawer>
  );
};
